import React from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  GraduationCap,
  FileText,
  Wallet,
  Wrench,
  Sprout,
  ArrowRight,
  CheckCircle,
  Sparkles,
  Handshake,
  Truck,
  LifeBuoy,
} from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import { colors, images, externalLinks, contact } from "../config/site";

const PROGRAMS = [
  {
    icon: GraduationCap,
    title: "Rent Readiness",
    body:
      "Foundational education that helps individuals and families prepare for successful, sustainable renting.",
  },
  {
    icon: Truck,
    title: "Move-In Support",
    body:
      "Practical guidance for the move-in process — utility setup, move-in checklists, deposits, and a smooth transition into a new home.",
  },
  {
    icon: FileText,
    title: "Lease Literacy",
    body:
      "Understanding lease agreements, rights, responsibilities, and how to advocate for yourself as a tenant.",
  },
  {
    icon: Wallet,
    title: "Budgeting",
    body:
      "Practical financial habits — budgeting for rent, utilities, savings, and unexpected expenses.",
  },
  {
    icon: Wrench,
    title: "Maintenance & Home Care",
    body:
      "Basic home upkeep, communicating maintenance needs, and protecting your home and security deposit.",
  },
  {
    icon: Sprout,
    title: "Homeownership Preparation",
    body:
      "Long-term planning, credit building, and pre-homeownership readiness for families preparing to buy.",
  },
  {
    icon: LifeBuoy,
    title: "Community Resources",
    body:
      "Connections to local services, benefits, partner organizations, and supports that strengthen housing stability.",
  },
];

const FOCUS_GROUPS = [
  "Women",
  "Low-to-moderate income families",
  "Individuals transitioning from shelters or temporary housing",
  "First-time renters",
  "Families preparing for future homeownership",
];

const ProgramsPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.cream }}>
      <Header />

      <main className="pt-24 pb-20">
        {/* Hero / Intro */}
        <section className="px-4 pb-16">
          <div className="container mx-auto max-w-6xl">
            <Badge
              className="mb-4 text-xs uppercase tracking-wider font-semibold"
              style={{ backgroundColor: "#EFE6D6", color: colors.terracottaDark }}
            >
              Housing Stability Programs
            </Badge>
            <h1
              className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6"
              style={{ color: colors.charcoal }}
            >
              Practical Education for{" "}
              <span style={{ color: colors.primary }}>Long-Term Housing Success</span>.
            </h1>
            <p className="text-lg md:text-xl max-w-3xl" style={{ color: colors.textMuted }}>
              Workshops and tools that help low-to-moderate income households
              — and anyone transitioning from shelters or temporary housing —
              become <strong>successful householders</strong> and stay housed long-term.
            </p>
          </div>
        </section>

        {/* Program Catalog */}
        <section className="px-4 pb-20">
          <div className="container mx-auto max-w-6xl">
            <div className="max-w-2xl mb-12">
              <p
                className="text-xs uppercase tracking-widest font-semibold mb-3"
                style={{ color: colors.terracotta }}
              >
                Our Programs
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold" style={{ color: colors.charcoal }}>
                Education that supports every step of the housing journey.
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {PROGRAMS.map(({ icon: Icon, title, body }) => (
                <div
                  key={title}
                  className="rounded-2xl p-6 shadow-sm hover:shadow-md transition-all"
                  style={{ backgroundColor: colors.white, borderTop: `3px solid ${colors.sage}` }}
                  data-testid={`program-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: colors.primary }}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{ color: colors.charcoal }}>
                    {title}
                  </h3>
                  <p className="text-sm" style={{ color: colors.textMuted }}>
                    {body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Current Delivery Model — Community Partnerships */}
        <section className="px-4 pb-20" style={{ backgroundColor: colors.ivory }}>
          <div className="container mx-auto max-w-6xl py-16">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge
                  className="mb-4 text-xs uppercase tracking-wider font-semibold"
                  style={{ backgroundColor: colors.primary, color: colors.white }}
                >
                  <Handshake className="w-3 h-3 mr-1.5" />
                  Current Delivery Model
                </Badge>
                <h2 className="text-3xl lg:text-4xl font-bold mb-5" style={{ color: colors.charcoal }}>
                  Community Partnerships &amp; Program Delivery
                </h2>
                <p className="mb-6" style={{ color: colors.textMuted }}>
                  Nonprofits, shelters, transitional housing programs, and housing agencies
                  contract with Dorsey Taylor Homes to deliver workshops, customized training,
                  and tenant success programming for their clients — a community-centered
                  model that supports our long-term vision for broader public access.
                </p>
                <Link to="/partnerships" data-testid="programs-partnerships-cta">
                  <Button
                    className="text-white rounded-full px-6 py-5 font-semibold"
                    style={{ backgroundColor: colors.terracotta }}
                  >
                    Become a Program Partner
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>

              <div className="rounded-3xl overflow-hidden shadow-md">
                <img
                  src={images.teaching}
                  alt="Anissa leading a community workshop session"
                  className="w-full h-[420px] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Who we serve */}
        <section className="px-4 pb-20">
          <div className="container mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1">
                <p
                  className="text-xs uppercase tracking-widest font-semibold mb-3"
                  style={{ color: colors.terracotta }}
                >
                  Who we serve
                </p>
                <h2 className="text-3xl font-bold" style={{ color: colors.charcoal }}>
                  Designed for the families our programs are built to lift.
                </h2>
              </div>
              <div className="lg:col-span-2">
                <ul className="grid sm:grid-cols-2 gap-3">
                  {FOCUS_GROUPS.map((g) => (
                    <li
                      key={g}
                      className="flex items-start gap-3 p-4 rounded-xl"
                      style={{ backgroundColor: colors.white, borderLeft: `3px solid ${colors.sage}` }}
                    >
                      <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: colors.sage }} />
                      <span className="font-medium" style={{ color: colors.charcoal }}>
                        {g}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Future Vision — Expansion */}
        <section className="px-4 pb-20">
          <div className="container mx-auto max-w-6xl">
            <div
              className="rounded-3xl p-8 lg:p-12 shadow-md grid lg:grid-cols-12 gap-10"
              style={{ backgroundColor: colors.white, borderLeft: `4px solid ${colors.terracotta}` }}
              data-testid="programs-future-vision"
            >
              <div className="lg:col-span-5">
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: "#EFE6D6", color: colors.terracottaDark }}
                  >
                    <Sprout className="w-6 h-6" />
                  </div>
                  <Badge
                    className="text-xs uppercase tracking-wider font-semibold"
                    style={{ backgroundColor: colors.gold, color: colors.charcoal }}
                  >
                    Looking Ahead
                  </Badge>
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold mb-4" style={{ color: colors.charcoal }}>
                  Expanding access for the broader community.
                </h2>
                <p style={{ color: colors.textMuted }}>
                  Dorsey Taylor Homes is actively working toward expanding these programs into broader
                  community-based initiatives that offer accessible and low-cost housing stability education
                  and support services.
                </p>
              </div>

              <div className="lg:col-span-7">
                <p className="font-semibold mb-4" style={{ color: colors.charcoal }}>
                  Our long-term vision includes creating increased access to:
                </p>
                <ul className="grid sm:grid-cols-2 gap-3 mb-6">
                  {[
                    "Community housing workshops",
                    "Accessible, low-cost tenant education",
                    "Housing transition support services",
                    "Move-in assistance programs",
                    "Pathways toward long-term homeownership",
                  ].map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2 p-3 rounded-xl"
                      style={{ backgroundColor: colors.cream }}
                    >
                      <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: colors.sage }} />
                      <span className="text-sm font-medium" style={{ color: colors.charcoal }}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm" style={{ color: colors.textMuted }}>
                  We are currently building partnerships, resources, and future funding opportunities to
                  help make these programs more widely accessible throughout the communities we serve.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Application / Get Involved */}
        <section className="px-4">
          <div className="container mx-auto max-w-4xl">
            <div
              className="rounded-3xl p-8 lg:p-10 shadow-sm text-center"
              style={{ backgroundColor: colors.white, borderTop: `4px solid ${colors.terracotta}` }}
            >
              <FileText className="w-10 h-10 mx-auto mb-4" style={{ color: colors.terracotta }} />
              <h3 className="text-2xl font-bold mb-3" style={{ color: colors.charcoal }}>
                Ready to get involved?
              </h3>
              <p className="mb-6 max-w-2xl mx-auto" style={{ color: colors.textMuted }}>
                Whether you&apos;re a current tenant, in a shelter or transitional housing, or
                working toward homeownership — share your interest and we&apos;ll connect you
                with the right program or partner.
              </p>
              <a
                href={externalLinks.applicationForm}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="programs-application-link"
                className="inline-flex items-center px-7 py-4 rounded-full font-semibold text-white text-base shadow-md hover:shadow-lg transition-all"
                style={{ backgroundColor: colors.terracotta }}
              >
                Fill Out Interest Form
                <ArrowRight className="ml-2 w-4 h-4" />
              </a>
              <p className="text-sm mt-6" style={{ color: colors.textMuted }}>
                Questions? Call{" "}
                <a href={contact.phoneHref} className="font-semibold underline" style={{ color: colors.primary }}>
                  {contact.phone}
                </a>{" "}
                or email{" "}
                <a href={contact.emailHref} className="font-semibold underline" style={{ color: colors.primary }}>
                  {contact.email}
                </a>
                .
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ProgramsPage;
